import { useEffect, useState, useCallback } from 'react';
import { formatPrice, formatPriceRange } from '../lib/format';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { LogOut, Download, Search, Eye, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { getLeads, getAnalyticsSummary, exportLeadsCSV, sendWeeklyDigest } from '../lib/api';
import OpsLayout from '../components/OpsLayout';

function TierBadge({ tier }) {
  const cls = tier === 'hot' ? 'bg-green-50 text-green-700 border-green-200' :
    tier === 'warm' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    'bg-slate-50 text-slate-600 border-slate-200';
  return <span className={`text-xs font-mono px-2 py-0.5 border ${cls}`} data-testid={`tier-badge-${tier}`}>{tier}</span>;
}

function StatusBadge({ status }) {
  const colors = {
    new: 'bg-blue-50 text-blue-700',
    contacted: 'bg-purple-50 text-purple-700',
    nurturing: 'bg-amber-50 text-amber-700',
    converted: 'bg-green-50 text-green-700',
    lost: 'bg-slate-100 text-slate-500',
  };
  return <span className={`text-xs px-2 py-0.5 ${colors[status] || colors.new}`} data-testid={`status-badge-${status}`}>{status}</span>;
}

export default function AdminLeadList() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ tier: '', status: '', q: '', page: 1 });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/ops/login'); return; }
    getAnalyticsSummary().then(r => setStats(r.data)).catch(() => {});
  }, [navigate]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.tier) params.tier = filters.tier;
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      params.page = filters.page;
      params.limit = 15;
      const r = await getLeads(params);
      setLeads(r.data.leads || []);
      setTotalPages(r.data.pages || 1);
      setTotal(r.data.total || 0);
    } catch (e) {
      if (e.response?.status === 401) navigate('/ops/login');
    }
    setLoading(false);
  }, [filters, navigate]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleExport = () => {
    const params = {};
    if (filters.tier) params.tier = filters.tier;
    if (filters.status) params.status = filters.status;
    exportLeadsCSV(params);
  };

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="admin-lead-list-page">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-6 md:px-12 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>Leads</h1>
            <p className="text-xs text-slate-400 mt-0.5">Manage buyer profiles and lead lifecycle</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={async () => { try { await sendWeeklyDigest(); alert('Weekly digest sent.'); } catch(e) { alert('Failed: ' + e.message); } }} className="rounded-lg h-9 text-xs gap-1.5" data-testid="send-digest-btn">
              <Mail className="w-3.5 h-3.5" /> Send Digest
            </Button>
            <Button variant="outline" onClick={handleExport} className="rounded-lg h-9 text-xs gap-1.5" data-testid="export-csv-btn">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Total Leads', value: stats.total_leads },
              { label: 'Hot', value: stats.hot_leads, color: 'text-green-700' },
              { label: 'Warm', value: stats.warm_leads, color: 'text-amber-700' },
              { label: 'Research', value: stats.research_leads, color: 'text-slate-600' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-slate-200 p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider">{s.label}</p>
                <p className={`font-mono text-2xl font-semibold mt-1 ${s.color || 'text-slate-900'}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="admin-filters">
          <Select value={filters.tier || "all"} onValueChange={(v) => setFilters(f => ({ ...f, tier: v === 'all' ? '' : v, page: 1 }))}>
            <SelectTrigger className="w-[140px] bg-white rounded-sm h-9 text-sm" data-testid="filter-tier">
              <SelectValue placeholder="All Tiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="hot">Hot</SelectItem>
              <SelectItem value="warm">Warm</SelectItem>
              <SelectItem value="research">Research</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v, page: 1 }))}>
            <SelectTrigger className="w-[160px] bg-white rounded-sm h-9 text-sm" data-testid="filter-status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="nurturing">Nurturing</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, phone..."
              className="pl-9 bg-white rounded-sm h-9 text-sm"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value, page: 1 }))}
              data-testid="filter-search"
            />
          </div>

          <Button variant="outline" onClick={handleExport} className="rounded-sm h-9 text-sm gap-1.5" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button variant="outline" onClick={async () => { try { await sendWeeklyDigest(); alert('Weekly digest sent to Karl.'); } catch(e) { alert('Failed: ' + e.message); } }} className="rounded-sm h-9 text-sm gap-1.5" data-testid="send-digest-btn">
            <Mail className="w-3.5 h-3.5" /> Send Digest
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 overflow-hidden" data-testid="leads-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-medium">Name</TableHead>
                <TableHead className="text-xs font-medium">Budget</TableHead>
                <TableHead className="text-xs font-medium">Timeline</TableHead>
                <TableHead className="text-xs font-medium">Tier</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="text-xs font-medium">Date</TableHead>
                <TableHead className="text-xs font-medium w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
              ) : leads.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">No leads yet.</TableCell></TableRow>
              ) : leads.map((lead) => {
                const leadId = lead.id;
                return (
                  <TableRow key={leadId} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/ops/leads/${leadId}`)} data-testid={`lead-row-${leadId}`}>
                    <TableCell className="text-sm font-medium">{lead.lead?.first_name || lead.lead?.email || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {lead.answers?.budget_min && lead.answers?.budget_max ?
                        `${formatPrice(lead.answers.budget_min)}–${formatPrice(lead.answers.budget_max)}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{{ '0_3': '0–3 Mo', '3_6': '3–6 Mo', '6_12': '6–12 Mo', 'researching': 'Research' }[lead.answers?.timeline] || lead.answers?.timeline || '—'}</TableCell>
                    <TableCell><TierBadge tier={lead.intent?.tier || 'research'} /></TableCell>
                    <TableCell><StatusBadge status={lead.lifecycle?.status || 'new'} /></TableCell>
                    <TableCell className="text-xs text-slate-400 font-mono">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      <Link to={`/ops/leads/${leadId}`} data-testid={`view-lead-${leadId}`}>
                        <Eye className="w-4 h-4 text-slate-400 hover:text-slate-700" />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4" data-testid="pagination">
            <p className="text-xs text-slate-400 font-mono">{total} leads total</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                className="rounded-sm h-8"
                data-testid="page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs font-mono text-slate-500">{filters.page} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={filters.page >= totalPages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                className="rounded-sm h-8"
                data-testid="page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
    </OpsLayout>
  );
}
